import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FiDownload, FiPrinter } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { useAuth } from '../../context/AuthContext';
import useCurrency from '../../hooks/useCurrency';
import PageLayout from '../../components/PageLayout';
import {
  FiltersCard, FilterGrid, FilterItem, FilterLabel, FilterSelect,
  KPIGrid, KPI, KL, KV, ToolbarRow, ExportBtn, ReportCard, RTable, Loading, Empty,
} from './reportStyles';
import { loadAssetReportData, exportRowsToExcel, printAssetReport } from '../../utils/assetReportHelper';
import { computeDepreciationForPeriod, appreciationOf, hasAppreciation } from '../../utils/depreciationHelper';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const DepreciationSchedule = () => {
  const { activeCompany, activeBranch } = useAuth();
  const { formatCurrency } = useCurrency();
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');

  const load = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try {
      const data = await loadAssetReportData(activeCompany.id, activeBranch);
      setAssets(data.assets);
      setCategories(data.categories);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [activeCompany, activeBranch]);
  useEffect(() => { load(); }, [load]);

  // Forward-looking 12-month depreciation projection per depreciable asset.
  const rows = useMemo(() => {
    return assets
      .filter(a => (a.status === 'Active' || a.status === 'FullyDepreciated') && a.depreciationMethod !== 'none')
      .filter(a => categoryFilter === 'all' || a.categoryId === categoryFilter)
      .map(a => {
        const calc = computeDepreciationForPeriod(a, { months: 12 });
        // Book value includes any Asset Appreciation (0 for most assets).
        const opening = round2((Number(a.acquisitionCost) || 0) + appreciationOf(a) - (Number(a.accumulatedDepreciation) || 0));
        return {
          asset: a,
          method: a.depreciationMethod === 'reducing_balance' ? 'WDV' : 'SLM',
          basis: a.depreciationMethod === 'reducing_balance' ? `${a.depreciationRate || 0}%` : `${a.usefulLifeYears || 0} yrs`,
          cost: round2(a.acquisitionCost),
          appr: appreciationOf(a),
          opening,
          annual: calc.amount,
          closing: calc.newNBV,
        };
      })
      .sort((x, y) => (x.asset.assetCode || '').localeCompare(y.asset.assetCode || ''));
  }, [assets, categoryFilter]);

  const totals = useMemo(() => rows.reduce((t, r) => ({
    cost: round2(t.cost + r.cost), appr: round2(t.appr + r.appr), opening: round2(t.opening + r.opening),
    annual: round2(t.annual + r.annual), closing: round2(t.closing + r.closing),
  }), { cost: 0, appr: 0, opening: 0, annual: 0, closing: 0 }), [rows]);
  // Asset Appreciation column only for companies that have any.
  const showAppr = useMemo(() => assets.some(hasAppreciation), [assets]);

  const columns = [
    { key: 'Code', label: 'Code' }, { key: 'Asset', label: 'Asset' }, { key: 'Category', label: 'Category' },
    { key: 'Method', label: 'Method' }, { key: 'Basis', label: 'Rate / Life' },
    { key: 'Cost', label: 'Cost', r: true },
    ...(showAppr ? [{ key: 'Appreciation', label: 'Appreciation', r: true }] : []),
    { key: 'Opening', label: 'Opening NBV', r: true },
    { key: 'Annual', label: 'Annual Dep.', r: true }, { key: 'Closing', label: 'Closing NBV', r: true },
  ];
  const buildRows = () => rows.map(r => ({
    Code: r.asset.assetCode, Asset: r.asset.assetName, Category: r.asset.categoryName,
    Method: r.method, Basis: r.basis, Cost: formatCurrency(r.cost),
    ...(showAppr ? { Appreciation: formatCurrency(r.appr) } : {}),
    Opening: formatCurrency(r.opening),
    Annual: formatCurrency(r.annual), Closing: formatCurrency(r.closing),
  }));

  const onPrint = () => printAssetReport({
    company: activeCompany, branch: activeBranch && activeBranch.id !== 'all' ? activeBranch : null,
    title: 'Depreciation Schedule (Next 12 Months)',
    filters: categoryFilter !== 'all' ? `Category: ${categories.find(c => c.id === categoryFilter)?.categoryName || ''}` : '',
    columns, rows: buildRows(),
    totals: { Cost: formatCurrency(totals.cost), ...(showAppr ? { Appreciation: formatCurrency(totals.appr) } : {}), Opening: formatCurrency(totals.opening), Annual: formatCurrency(totals.annual), Closing: formatCurrency(totals.closing) },
    kpis: [
      { label: 'Depreciable Assets', value: String(rows.length) },
      { label: 'Current NBV', value: formatCurrency(totals.opening) },
      { label: 'Projected Annual Dep.', value: formatCurrency(totals.annual), highlight: true },
    ],
  });

  return (
    <PageLayout pageTitle="Depreciation Schedule" breadcrumb="Reports / Fixed Assets / Depreciation Schedule">
      <FiltersCard>
        <FilterGrid>
          <FilterItem>
            <FilterLabel>Category</FilterLabel>
            <FilterSelect value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option value="all">All Categories</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.categoryName}</option>)}
            </FilterSelect>
          </FilterItem>
        </FilterGrid>
      </FiltersCard>

      <KPIGrid>
        <KPI><KL>Depreciable Assets</KL><KV>{rows.length}</KV></KPI>
        <KPI><KL>Current NBV</KL><KV>{formatCurrency(totals.opening)}</KV></KPI>
        <KPI $highlight><KL>Projected Annual Depreciation</KL><KV>{formatCurrency(totals.annual)}</KV></KPI>
      </KPIGrid>

      <ToolbarRow>
        <h3 style={{ color: colors.text.secondary }}>Forward 12-month projection from current balances</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <ExportBtn onClick={() => exportRowsToExcel('DepreciationSchedule', 'Schedule', buildRows())}><FiDownload /> Excel</ExportBtn>
          <ExportBtn onClick={onPrint}><FiPrinter /> Print</ExportBtn>
        </div>
      </ToolbarRow>

      {loading ? <Loading>Loading...</Loading> : rows.length === 0 ? <Empty>No depreciable assets.</Empty> : (
        <ReportCard>
          <RTable>
            <thead>
              <tr>
                <th>Code</th><th>Asset</th><th>Category</th><th>Method</th><th>Rate / Life</th>
                <th className="r">Cost</th>{showAppr && <th className="r">Appreciation</th>}<th className="r">Opening NBV</th><th className="r">Annual Dep.</th><th className="r">Closing NBV</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.asset.id}>
                  <td><strong>{r.asset.assetCode}</strong></td>
                  <td>{r.asset.assetName}</td>
                  <td>{r.asset.categoryName}</td>
                  <td>{r.method}</td>
                  <td>{r.basis}</td>
                  <td className="r">{formatCurrency(r.cost)}</td>
                  {showAppr && <td className="r">{formatCurrency(r.appr)}</td>}
                  <td className="r">{formatCurrency(r.opening)}</td>
                  <td className="r">{formatCurrency(r.annual)}</td>
                  <td className="r">{formatCurrency(r.closing)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={5}>Total</td>
                <td className="r">{formatCurrency(totals.cost)}</td>
                {showAppr && <td className="r">{formatCurrency(totals.appr)}</td>}
                <td className="r">{formatCurrency(totals.opening)}</td>
                <td className="r">{formatCurrency(totals.annual)}</td>
                <td className="r">{formatCurrency(totals.closing)}</td>
              </tr>
            </tfoot>
          </RTable>
        </ReportCard>
      )}
    </PageLayout>
  );
};

export default DepreciationSchedule;
